import React from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

/**
 * Terms of Service.
 *
 * Two commercial facts drive this page and must not drift from the rest of the
 * site: there is no free trial (access begins only on payment), and all sales
 * are final (no refunds). The pricing page, help centre and hero copy state the
 * same thing - change them together or not at all.
 */
const TermsPage = () => {
  return (
    <div className="min-h-screen">
      <Header />

      <main className="pt-24 pb-16">
        <section className="section">
          <div className="max-w-3xl mx-auto">
            <h1 className="heading-1 mb-4">Terms of Service</h1>
            <p className="text-[#83928c] mb-8">Last updated: September 2026</p>

            <div className="prose prose-lg max-w-none">
              <div className="space-y-8">
                <div>
                  <h2 className="heading-3 mb-4">1. Acceptance of Terms</h2>
                  <p className="text-[#83928c]">
                    Tenses is operated by TALENTSCALE INDIA PRIVATE LIMITED (&quot;Tenses&quot;,
                    &quot;we&quot;, &quot;us&quot;). By creating an account or using the platform you
                    agree to be bound by these Terms of Service and all applicable laws. If you do
                    not agree with any part of these terms, please do not use the service.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">2. Eligibility and Accounts</h2>
                  <p className="text-[#83928c] mb-4">
                    You must provide accurate details when registering and keep them up to date. You
                    are responsible for maintaining the confidentiality of your credentials and for
                    all activity under your account. Notify us immediately at support@tenses.ai if
                    you believe your account has been used without your permission.
                  </p>
                  <p className="text-[#83928c]">
                    Accounts are personal to you. Sharing an account, or allowing more than one
                    person to practise or take assessments under a single subscription, is not
                    permitted and may result in suspension without refund.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">3. No Free Trial</h2>
                  <p className="text-[#83928c]">
                    We do not offer a free trial, free tier or trial period. Registering an account
                    on its own does not unlock any learning module, assessment or the AI interview
                    simulator. Access to the platform begins only once a plan has been purchased and
                    payment has been confirmed.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">4. Plans, Pricing and Payment</h2>
                  <p className="text-[#83928c] mb-4">
                    Plans are sold as fixed-duration access — currently 3, 6 and 12 months — and are
                    paid in full and in advance. Prices are shown in Indian Rupees and are exclusive
                    of GST, which is charged at the applicable rate (currently 18%) and shown before
                    you pay.
                  </p>
                  <p className="text-[#83928c] mb-4">
                    Payments are processed by our payment gateway, Razorpay. We do not receive or
                    store your card or banking details. Your access begins as soon as the payment is
                    confirmed and ends automatically at the close of the purchased period.
                  </p>
                  <p className="text-[#83928c]">
                    Plans do not renew automatically. If you want to continue after your access
                    period ends, you purchase a new plan. Prices may change at any time, but a change
                    never affects a plan you have already paid for.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">5. No Refunds</h2>
                  <p className="text-[#83928c] mb-4">
                    <strong className="text-[#1a1a2e]">All sales are final.</strong> Because your
                    plan unlocks the full platform — every learning module, all assessments and the
                    AI interview simulator — the moment payment is confirmed, purchases are
                    non-refundable. This applies whether or not you go on to use the platform, and
                    however much of your access period remains.
                  </p>
                  <p className="text-[#83928c] mb-4">
                    We do not offer partial refunds, pro-rata credits for unused time, transfers of a
                    plan to another person, or cancellation with a refund part-way through a period.
                    Please make sure a plan suits you before you buy it.
                  </p>
                  <p className="text-[#83928c]">
                    The only exceptions are a genuine duplicate charge, or a technical failure on our
                    side that prevented access for a sustained period and that we were unable to
                    resolve. Write to support@tenses.ai with your registered email address and we
                    will investigate. Nothing here limits any right you may have under applicable
                    consumer law that cannot be waived by agreement.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">6. Use of the Platform</h2>
                  <p className="text-[#83928c] mb-4">
                    We grant you a personal, non-transferable, non-exclusive licence to use Tenses
                    for your own learning for the duration of your plan. This is a licence to use the
                    service, not a transfer of ownership. You may not:
                  </p>
                  <ul className="list-disc list-inside text-[#83928c] space-y-2">
                    <li>Copy, redistribute, publish or resell any part of our content</li>
                    <li>Use the platform or its content for any commercial purpose</li>
                    <li>Reverse engineer, scrape, or attempt to extract our question banks or models</li>
                    <li>Share, sell or transfer your account or access credentials</li>
                    <li>Remove or obscure any copyright or proprietary notice</li>
                    <li>Interfere with the platform&apos;s operation, security or other users&apos; use of it</li>
                  </ul>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">7. Speech and Written Submissions</h2>
                  <p className="text-[#83928c]">
                    Several modules record your voice or accept written answers so they can be
                    scored. You retain ownership of what you submit and grant us only the licence
                    needed to process it, score it and show you your results. How this data is
                    handled is described in our Privacy Policy.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">8. TPI Certification</h2>
                  <p className="text-[#83928c]">
                    The Tenses Proficiency Index (TPI) is awarded on the basis of your performance in
                    our own assessments. It reflects our assessment of your English communication
                    skills and is not an official, government-recognised or internationally
                    standardised language qualification. We make no promise that any employer or
                    institution will accept or recognise a TPI score, and a purchase is not a
                    guarantee of certification or of any particular score.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">9. Availability</h2>
                  <p className="text-[#83928c]">
                    We work to keep Tenses available and accurate, but the service is provided on an
                    &quot;as is&quot; and &quot;as available&quot; basis. We may need to suspend
                    access for maintenance, updates or reasons beyond our control, and we may add,
                    change or withdraw features over time.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">10. Suspension and Termination</h2>
                  <p className="text-[#83928c]">
                    We may suspend or terminate an account that breaches these terms — including
                    account sharing, attempts to extract our content, or misuse of the platform.
                    Where an account is terminated for a breach, no refund is due. You may stop
                    using the service and request deletion of your account at any time; as set out
                    in section 5, this does not entitle you to a refund of the remaining period.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">11. Limitation of Liability</h2>
                  <p className="text-[#83928c]">
                    To the maximum extent permitted by law, Tenses shall not be liable for any
                    indirect, incidental or consequential loss arising from your use of, or
                    inability to use, the service — including any employment, academic or commercial
                    outcome. Where liability cannot be excluded, it is limited to the amount you
                    paid us for the plan in question.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">12. Governing Law</h2>
                  <p className="text-[#83928c]">
                    These terms are governed by the laws of India, and the courts of India shall have
                    exclusive jurisdiction over any dispute arising from them or from your use of the
                    platform.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">13. Changes to These Terms</h2>
                  <p className="text-[#83928c]">
                    We may revise these terms from time to time. The revision date at the top of this
                    page always reflects the current version, and continuing to use Tenses after a
                    change means you accept the updated terms.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">14. Contact</h2>
                  <p className="text-[#83928c]">
                    Questions about these Terms of Service can be sent to legal@tenses.ai, or through
                    our Contact page. For help with your account or a payment, write to
                    support@tenses.ai.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TermsPage;
